import React, { useState } from 'react';
import { 
  Box, Container, Typography, TextField, Button, Paper,
  Card, CardContent, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Support: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Support request:', formData);
  };

  const faqs = [
    {
      question: 'How do I get started?',
      answer: 'Sign up for a free account, get your API key from the dashboard, and start making API calls. Check our Quick Start guide in the documentation.'
    },
    {
      question: 'What happens if I exceed my plan limits?',
      answer: "We'll notify you when you're approaching your limit. You can upgrade your plan anytime or purchase additional credits."
    },
    {
      question: 'Do you store my screenshots?',
      answer: 'Screenshots are stored for 30 days by default. Pro and Enterprise users can configure custom retention policies.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your subscription at any time from your account settings. No questions asked.'
    },
  ];

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 64px - 300px)' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'black', color: 'white', py: { xs: 4, sm: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ color: 'white', fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
            How Can We Help You?
          </Typography>
          <Typography variant="h6" align="center" sx={{ color: 'rgba(255,255,255,0.8)', px: { xs: 2, sm: 0 } }}>
            Get answers to your questions or reach out to our support team
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 4
        }}>
          {/* Contact Form */}
          <Box>
            <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Typography variant="h5" gutterBottom fontWeight={600} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Contact Support
              </Typography>
              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      label="Category"
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    >
                      <MenuItem value="general">General Inquiry</MenuItem>
                      <MenuItem value="technical">Technical Support</MenuItem>
                      <MenuItem value="billing">Billing Question</MenuItem>
                      <MenuItem value="feature">Feature Request</MenuItem>
                      <MenuItem value="bug">Bug Report</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  multiline
                  rows={6}
                  required
                />

                <Button 
                  type="submit" 
                  variant="contained" 
                  size="large"
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Send Message
                </Button>
              </Box>
            </Paper>
          </Box>

          {/* Sidebar */}
          <Box>
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight={600} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                Other Ways to Reach Us
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <EmailIcon />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>Email</Typography>
                    <Typography variant="body2" color="text.secondary">support@screenshot.app</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <ChatIcon />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>Live Chat</Typography>
                    <Typography variant="body2" color="text.secondary">Available Mon-Fri, 9am-6pm EST</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <MenuBookIcon />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>Documentation</Typography>
                    <Typography variant="body2" color="text.secondary">Check our comprehensive API docs</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccessTimeIcon />
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                  Response Time
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2"><strong>Free:</strong></Typography>
                  <Typography variant="body2" color="text.secondary">48 hours</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2"><strong>Pro:</strong></Typography>
                  <Typography variant="body2" color="text.secondary">24 hours</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2"><strong>Enterprise:</strong></Typography>
                  <Typography variant="body2" color="text.secondary">4 hours</Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* FAQ Section */}
        <Typography variant="h4" align="center" gutterBottom fontWeight={700} sx={{ mt: { xs: 6, md: 8 }, mb: 4, fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' } }}>
          Frequently Asked Questions
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3
        }}>
          {faqs.map((faq, index) => (
            <Card key={index} sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom fontWeight={600} sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                  {faq.question}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {faq.answer}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Support;
